import { createDeferred } from '@forest/native-services'
import { createSignal, untrack, type Accessor } from 'solid-js'
import { mark, reportBootSummary } from './perf'

// Web-side backstop for stalled hydration: past this, counted from bootstrap
// success, the boot skeleton gives way to whatever has settled, unsettled
// slots showing their own slot skeletons (GLOSSARY.md "Shop reveal").
export const REVEAL_DEADLINE_MS = 10_000

// Which reveal path fired; the perf summary reports it.
export type RevealReason = 'deadline' | 'landing-settled'

export interface RevealGate {
  // Resolves at the reveal; the Shop hydration order's second stage waits on
  // it (GLOSSARY.md "Shop hydration order").
  released: Promise<void>
  reveal: (reason: RevealReason) => boolean
  // Reactive: true once the boot skeleton has been asked to give way.
  revealed: Accessor<boolean>
}

// The single-flight Shop reveal (GLOSSARY.md; ADR 0048): whichever path
// fires first — reveal surface settled or deadline — swaps the boot skeleton
// for the real Shop. Later calls no-op and report false. It is not
// pageReady: the native loading cover was dismissed at boot.
export function createRevealGate(): RevealGate {
  const [revealed, setRevealed] = createSignal(false)
  const released = createDeferred()
  return {
    released: released.promise,
    reveal: (reason) => {
      // Untracked: reveal() runs inside effects that must not re-run on the
      // reveal itself.
      if (untrack(revealed)) return false
      mark('reveal', { reason })
      reportBootSummary(reason)
      setRevealed(true)
      released.done()
      return true
    },
    revealed,
  }
}
